import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
} from "recharts";
import ThemedChartTooltip, { ThemedTooltipBox } from "../../shared/ThemedChartTooltip";
import InfoTip from "../../shared/InfoTip";
import { fmtCurrency } from "../../shared/format.js";

/**
 * DualAxisBandChart — stay_and_rate_tolerance evidence component.
 *
 * Bands on X in array order. avg_nights as bars (left axis),
 * avg_nightly_rate as a line (right axis). Tooltip shows bookings.
 * Grey bands with fewer than 30 bookings.
 *
 * Spec references: §7 (stay_and_rate_tolerance).
 */

const fmtNum = (v) => Number(v ?? 0).toLocaleString();

const BAR_COLOR = "#3B82F6";
const LINE_COLOR = "#F97316";
const GREY_COLOR = "#D1D5DB";

const BAND_KEYS = ["temp_band_f", "precip_probability_band"];
const getBandKey = (bands) => {
  if (!bands || bands.length === 0) return BAND_KEYS[0];
  return BAND_KEYS.find((k) => bands[0][k] != null) || BAND_KEYS[0];
};

/** Chart title based on band key. */
const getChartTitle = (bandKey) => {
  if (bandKey === "precip_probability_band") return "Stay & Rate by Rain Probability";
  return "Stay & Rate by Temperature";
};

/** InfoTip text based on band key. */
const getInfoTipText = (bandKey) => {
  if (bandKey === "precip_probability_band") {
    return "Average length of stay (bars) and nightly rate (line) for each rain probability band. Greyed bands have fewer than 30 bookings and are not part of the trend.";
  }
  return "Average length of stay (bars) and nightly rate (line) for each temperature band. Greyed bands have fewer than 30 bookings and are not part of the trend.";
};

/** Unit suffix for band labels based on band key. */
const bandSuffix = (bandKey) => {
  if (bandKey === "precip_probability_band") return "%";
  if (bandKey === "temp_band_f") return "°F";
  return "";
};

/** Format a band label with its unit suffix.
 *  "75-84" → "75-84°F", "95+" → "95°F+"
 *  "0-19" → "0-19%", "80-100" → "80-100%"
 */
const formatBandLabel = (label, bandKey) => {
  const suffix = bandSuffix(bandKey);
  if (!suffix || !label) return label;
  if (suffix === "°F" && label.endsWith("+")) {
    return `${label.slice(0, -1)}${suffix}+`;
  }
  return `${label}${suffix}`;
};

const DualAxisBandChart = ({ panel }) => {
  const f = panel.facts || {};
  const bands = f.bands || [];

  if (bands.length === 0) return null;

  const bandKey = getBandKey(bands);
  const chartTitle = getChartTitle(bandKey);
  const infoTipText = getInfoTipText(bandKey);

  const chartData = bands.map((b) => ({
    ...b,
    _bandKey: bandKey,
    label: b[bandKey],
    lowConfidence: (b.bookings || 0) < 30,
  }));

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <span>{chartTitle}</span>
            <InfoTip text={infoTipText} />
          </h3>
          {f.window_days && f.sessions != null && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Last {f.window_days} days · {fmtNum(f.sessions)} visits
            </p>
          )}
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={{ stroke: "#374151" }}
              tickLine={false}
              tickFormatter={(v) => formatBandLabel(v, bandKey)}
            />
            <YAxis
              yAxisId="nights"
              orientation="left"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <YAxis
              yAxisId="rate"
              orientation="right"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `$${fmtNum(v)}`}
              width={48}
            />
            <ThemedChartTooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const data = payload[0]?.payload;
                if (!data) return null;
                const bk = data._bandKey;
                return (
                  <ThemedTooltipBox title={formatBandLabel(data[bk], bk)}>
                    <div>{fmtNum(data.bookings)} bookings</div>
                    <div>Avg stay: {data.avg_nights} nights</div>
                    <div>Avg rate: {fmtCurrency(data.avg_nightly_rate)}</div>
                  </ThemedTooltipBox>
                );
              }}
            />
            <Bar
              yAxisId="nights"
              dataKey="avg_nights"
              radius={[4, 4, 0, 0]}
              maxBarSize={48}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={index}
                  fill={entry.lowConfidence ? GREY_COLOR : BAR_COLOR}
                  opacity={entry.lowConfidence ? 0.5 : 1}
                />
              ))}
            </Bar>
            <Line
              yAxisId="rate"
              type="monotone"
              dataKey="avg_nightly_rate"
              stroke={LINE_COLOR}
              strokeWidth={2}
              dot={{ r: 3, fill: LINE_COLOR }}
              activeDot={{ r: 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Legend — centered below the graph */}
        <div className="flex items-center justify-center gap-4 text-[11px] text-gray-500 dark:text-gray-400 mt-3">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-blue-500" />
            Avg nights
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-0.5 bg-orange-500" />
            Avg nightly rate
          </span>
        </div>

        {/* Grey-out legend */}
        {chartData.some((d) => d.lowConfidence) && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center mt-2">
            Greyed bands have fewer than 30 bookings.
          </p>
        )}
      </div>
    </div>
  );
};

export default DualAxisBandChart;